/**
 * Accordion Box - Auto-open accordion items on hash navigation
 * Handles smooth scroll to elements inside accordion with automatic item expansion
 *
 * @module accordion-box
 */

const ACCORDION_BOX_SELECTOR = '.b-accordion-box';
const ACCORDION_ITEM_SELECTOR = '.wp-block-accordion-item';
const ACCORDION_BUTTON_SELECTOR = '.wp-block-accordion-heading__toggle';

/**
 * Configuration object for accordion behavior
 * @constant {Object}
 */
const ACCORDION_CONFIG = {
	/**
	 * Extra spacing below sticky header in pixels
	 * Prevents content from being hidden under the header
	 */
	STICKY_HEADER_PADDING: 20,

	/**
	 * Delay in milliseconds for initial hash scroll on page load
	 * Allows layout to stabilize before scrolling
	 */
	INITIAL_SCROLL_DELAY: 300,

	/**
	 * Delay after opening accordion before scrolling
	 * Allows accordion animation to complete
	 */
	ACCORDION_OPEN_DELAY: 350,
};

/**
 * Accordion Box class
 * Handles hash navigation with accordion auto-opening
 * @class
 */
class AccordionBox {

	/**
	 * Creates an instance of AccordionBox
	 * @param {HTMLElement} block - The accordion box element
	 */
	constructor( block ) {

		// Prevent double initialization
		if ( block.dataset.accordionBoxInitialized ) {

			return;

		}

		block.dataset.accordionBoxInitialized = 'true';
		this.block = block;
		this.init();

	}

	/**
	 * Initializes the accordion box functionality
	 * @return {void}
	 */
	init() {

		try {

			this.attachEventListeners();
			this.handleInitialHash();

		} catch ( error ) {

			// eslint-disable-next-line no-console
			console.error( 'Accordion Box initialization failed:', error );

		}

	}

	/**
	 * Get the current sticky header offset.
	 * @return {number} Offset in pixels.
	 */
	getStickyHeaderOffset() {

		const header = document.querySelector( '.p-header' );
		if ( !header ) {

			return 0;

		}

		const headerStyles = window.getComputedStyle( header );
		const isSticky =
			headerStyles.position === 'sticky' ||
			headerStyles.position === 'fixed' ||
			header.classList.contains( 'p-header--sticky-scroll' );

		return isSticky
			? header.offsetHeight + ACCORDION_CONFIG.STICKY_HEADER_PADDING
			: 0;

	}

	/**
	 * Smooth scroll to a target element with header offset.
	 * @param {HTMLElement} target - Scroll target.
	 * @return {void}
	 */
	scrollToElement( target ) {

		if ( !target || !target.getBoundingClientRect ) {

			return;

		}

		try {

			const offset = this.getStickyHeaderOffset();
			const targetPosition =
				target.getBoundingClientRect().top + window.scrollY - offset;

			window.scrollTo( {
				top: targetPosition,
				behavior: 'smooth',
			} );

			// Focus management for accessibility (WCAG 2.4.3)
			if ( !target.hasAttribute( 'tabindex' ) ) {

				target.setAttribute( 'tabindex', '-1' );

			}

			target.style.outline = 'none';

			setTimeout( () => {

				if ( target && typeof target.focus === 'function' ) {

					target.focus( { preventScroll: true } );

				}

			}, 100 );

		} catch ( error ) {

			// eslint-disable-next-line no-console
			console.error( 'Scroll to element failed:', error );

		}

	}

	/**
	 * Find the accordion item that contains the target element
	 * @param {HTMLElement} target - Target element
	 * @return {HTMLElement|null} Accordion item or null
	 */
	findAccordionItem( target ) {

		return target.closest( ACCORDION_ITEM_SELECTOR );

	}

	/**
	 * Check if accordion item is open
	 * @param {HTMLElement} accordionItem - Accordion item element
	 * @return {boolean} True if open
	 */
	isAccordionOpen( accordionItem ) {

		return accordionItem.classList.contains( 'is-open' );

	}

	/**
	 * Open accordion item by clicking its button
	 * @param {HTMLElement} accordionItem - Accordion item to open
	 * @return {void}
	 */
	openAccordionItem( accordionItem ) {

		if ( this.isAccordionOpen( accordionItem ) ) {

			return;

		}

		const button = accordionItem.querySelector( ACCORDION_BUTTON_SELECTOR );
		if ( button ) {

			button.click();

		}

	}

	/**
	 * Handle hash change and navigate to target
	 * @param {string} hash - Hash string (with #)
	 * @return {void}
	 */
	handleHashNavigation( hash ) {

		if ( !hash || hash === '#' ) {

			return;

		}

		try {

			const targetId = hash.substring( 1 );
			const target = document.getElementById( targetId );

			if ( !target ) {

				return;

			}

			// Check if target is inside an accordion item
			const accordionItem = this.findAccordionItem( target );

			if ( accordionItem ) {

				// Open accordion first
				this.openAccordionItem( accordionItem );

				// Wait for accordion animation, then scroll
				setTimeout( () => {

					this.scrollToElement( target );

				}, ACCORDION_CONFIG.ACCORDION_OPEN_DELAY );

			} else {

				// Target not in accordion, just scroll
				this.scrollToElement( target );

			}

		} catch ( error ) {

			// eslint-disable-next-line no-console
			console.error( 'Hash navigation failed:', error );

		}

	}

	/**
	 * Handle initial page load hash
	 * @return {void}
	 */
	handleInitialHash() {

		if ( !window.location.hash ) {

			return;

		}

		setTimeout( () => {

			this.handleHashNavigation( window.location.hash );

		}, ACCORDION_CONFIG.INITIAL_SCROLL_DELAY );

	}

	/**
	 * Attach event listeners
	 * @return {void}
	 */
	attachEventListeners() {

		// Listen for hash changes (browser back/forward, anchor clicks)
		window.addEventListener( 'hashchange', () => {

			this.handleHashNavigation( window.location.hash );

		} );

		// Listen for clicks on links within this accordion box
		this.block.addEventListener( 'click', ( e ) => {

			const link = e.target.closest( 'a[href^="#"]' );
			if ( link && link.hash ) {

				e.preventDefault();
				window.location.hash = link.hash;

			}

		} );

	}

}

document.querySelectorAll( ACCORDION_BOX_SELECTOR ).forEach( ( block ) => {

	new AccordionBox( block );

} );
